import React, { useEffect, useState, useCallback, useMemo, memo } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { RiFullscreenLine, RiFullscreenExitLine } from "react-icons/ri";
import { BiShoppingBag } from "react-icons/bi";
import SignOut from "../admin/SignOut";
import ChangePassword from "../Authentication/ChangePassword";
import "./Header.css";

// Memoized Modal Component
const Modal = memo(({ children, closeModal }) => (
  <div className="modal-overlay">
    <div className="modal-content">
      <button className="close-button" onClick={closeModal}>
        X
      </button>
      {children}
    </div>
  </div>
));

// Memoized UserMenu Component
const UserMenu = memo(({ currentUser, balance, openModal, isDropdownOpen, toggleDropdown, isLoggingOut, setIsLoggingOut }) => (
  <div className="user-menu">
    <div className="profile-section">
      <button 
        className="profile-button"
        onClick={toggleDropdown}
        disabled={isLoggingOut}
      >
        <img
          className="profile-avatar"
          src={currentUser.avatar}
          alt="profile"
        />
        <span className="profile-username">{currentUser.username}</span>
        <span className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}>▼</span>
      </button>
      {isDropdownOpen && (
        <div className="profile-dropdown">
          <div className="dropdown-item">
            <button onClick={openModal} className="dropdown-button">
              Change Password
            </button>
          </div>
          <div className="dropdown-divider"></div>
          <div className="dropdown-item">
            <SignOut isLoggingOut={isLoggingOut} onLogoutStateChange={setIsLoggingOut} />
          </div>
        </div>
      )}
    </div>
    <div className="balance-section">
      <BiShoppingBag className="shopping-icon" />
      <div className="balance-info notranslate">Your balance is {balance}</div>
      <Link to="/casheir" className="dashboard-button">
        Dashboard
      </Link>
    </div>
  </div>
));

// Memoized LanguageSwitcher Component
const LanguageSwitcher = memo(() => {
  const handleLanguageChange = useCallback((event) => {
    const lang = event.target.value;
    const languageSelect = document.querySelector("select.goog-te-combo");
    
    if (languageSelect) {
      languageSelect.value = lang;
      languageSelect.dispatchEvent(new Event("change"));
    }
  }, []);

  return (
    <div className="language-switcher">
      <select className="language-dropdown" onChange={handleLanguageChange}>
        <option value="en" data-lang="en">english</option>
        <option value="am" data-lang="am">amharic</option>
        <option value="ti" data-lang="ti">tigrigna</option>
        <option value="om" data-lang="om">oromigna</option>
        <option value="so" data-lang="so">somali</option>
      </select>
    </div>
  );
});

// Main Header Component
const Header = memo(({ balancewon, cardCount, selectedCards }) => {
  const { currentUser } = useSelector((state) => state.user);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [balance, setBalance] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [isCheckedMarquee, setIsCheckedMarquee] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Memoized fetchBalance function
  const fetchBalance = useCallback(async () => {
    try {
      const res = await axios.get(`/api/credit/${currentUser._id}/balance`);
      setBalance(res.data.balance);
    } catch (err) {
      console.error("Error fetching balance:", err);
    }
  }, [currentUser?._id]);

  useEffect(() => {
    if (currentUser) {
      fetchBalance();
      // Set up interval for balance updates (optional)
      const intervalId = setInterval(fetchBalance, 30000); // Update every 30 seconds
      return () => clearInterval(intervalId); // Cleanup on unmount
    }
  }, [currentUser, fetchBalance]);

  // Memoized event handlers
  const toggleFullScreen = useCallback(() => {
    if (!isFullScreen) {
      document.documentElement.requestFullscreen().catch(err => {
        console.error("Error attempting to enable fullscreen:", err);
      });
    } else {
      document.exitFullscreen();
    }
    setIsFullScreen(!isFullScreen);
  }, [isFullScreen]);

  const openModal = useCallback(() => {
    setIsModalOpen(true);
    setIsDropdownOpen(false); // Close dropdown when opening modal
  }, []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(!isDropdownOpen);
  }, [isDropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.profile-section')) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  const handleCheckboxChange = useCallback(() => {
    setIsChecked(!isChecked);
  }, [isChecked]);

  const handleCheckboxChangeMarquee = useCallback(() => {
    setIsCheckedMarquee(!isCheckedMarquee);
  }, [isCheckedMarquee]);

  // Memoized marquee items
  const marqueeItems = useMemo(() => {
    return selectedCards.map((number) => (
      <span key={number} className="marquee-item">{number}</span>
    ));
  }, [selectedCards]);

  // Memoized win amount display
  const winAmountDisplay = useMemo(() => (
    <fieldset className="win-amount-fieldset">
      <legend>Win Amount</legend>
      <span className="money-icon">
        <label>
          <input 
            type="checkbox" 
            checked={isChecked} 
            onChange={handleCheckboxChange} 
          />
          total
        </label>
        <label>
          <input 
            type="checkbox" 
            checked={isCheckedMarquee} 
            onChange={handleCheckboxChangeMarquee} 
          />
          cards
        </label>
        {isChecked && (
          <div className="newclass">
            Players: {cardCount}
          </div>
        )}
        <p className="balance-text">{balancewon} ETB</p>
      </span>
    </fieldset>
  ), [isChecked, isCheckedMarquee, cardCount, balancewon, handleCheckboxChange, handleCheckboxChangeMarquee]);

  return (
    <header className="header">
      <div className="container">
        <div className="left-section">
          <Link to="/">
            <div className="tw-flex-col tw-gap-0 tw-items-center tw-text-center">
              <img src={currentUser.imageUrl} className="logo" alt="logoImage" />
            </div>
          </Link>
        </div>

        <div className="pose">
          {winAmountDisplay}
          {isCheckedMarquee && (
            <div className="marquee">
              <div className="marquee-content">
                {marqueeItems}
              </div>
            </div>
          )}
        </div>

        <div className="right-section">
          <button onClick={toggleFullScreen} className="fullscreen-button">
            {isFullScreen ? <RiFullscreenExitLine /> : <RiFullscreenLine />}
          </button>
          {currentUser ? (
            <UserMenu
              currentUser={currentUser}
              balance={balance}
              openModal={openModal}
              isDropdownOpen={isDropdownOpen}
              toggleDropdown={toggleDropdown}
              isLoggingOut={isLoggingOut}
              setIsLoggingOut={setIsLoggingOut}
            />
          ) : (
            <Link to="/sign-in" className="sign-in-button">
              Sign In
            </Link>
          )}
          <LanguageSwitcher />
        </div>
      </div>

      {isModalOpen && (
        <Modal closeModal={closeModal}>
          <ChangePassword />
        </Modal>
      )}
    </header>
  );
});

export default Header;